/*
 * This file is part of the Det Norske Teatret Nettside 2019 application.
 *
 * (c) APT AS
 *
 * For the full copyright and license information, please view the LICENSE
 * file that was distributed with this source code.
 */

import React, {
  forwardRef,
  useRef,
  useState,
  useImperativeHandle,
} from 'react';
import From, {
  type FormHandle,
  type FormProps,
  type FormState,
} from 'components/Form/Form';
import Loader from 'components/Loader/Loader';
import api from 'utils/api';
import scrollTo from 'utils/scrollTo';
import useCsrf from 'effects/csrf';
import './Newsletter.scss';

const DEFAULT_FORM: NonNullable<NewsletterProps['form']> = {};
const DEFAULT_ON_CHANGE = () => {};
const DEFAULT_ON_LOADING = () => {};
const DEFAULT_ON_ERROR = () => {};
const DEFAULT_ON_SUCCESS = () => {};

export interface NewsletterHandle {
  focus: () => void;
}

export interface NewsletterProps {
  disabled?: boolean;
  inactive?: boolean;
  action: string;
  form?: Pick<
    FormProps,
    'className' | 'fields' | 'errors' | 'required' | 'submit'
  >;
  receipt?: string;
  onChange?: FormProps['onChange'];
  onLoading?: (loading: boolean) => void;
  onError?: (error: string) => void;
  onSuccess?: () => void;
}

/**
 * This is the Newsletter component.
 *
 * @author Thomas Sømoen <thomas@apt.no>
 */

const Newsletter = forwardRef<NewsletterHandle, NewsletterProps>(
  function Newsletter(
    {
      disabled = false,
      inactive = false,
      action,
      form = DEFAULT_FORM,
      receipt: receiptText = '<p>Thank you for your interest.</p>',
      onChange = DEFAULT_ON_CHANGE,
      onLoading = DEFAULT_ON_LOADING,
      onError: onErrorCallback = DEFAULT_ON_ERROR,
      onSuccess = DEFAULT_ON_SUCCESS,
    },
    ref
  ) {
    const containerRef = useRef<HTMLDivElement>(null);
    const fromRef = useRef<FormHandle>(null);
    const [csrf, csrfActions] = useCsrf();
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [receipt, setReceipt] = useState<boolean | null>(false);

    useImperativeHandle(ref, () => ({
      focus: () => {
        if (fromRef.current) {
          fromRef.current.focus();
        }
      },
    }));

    function onError() {
      if (containerRef.current) {
        const firstFieldError = containerRef.current.querySelector<HTMLElement>(
          '.field-row.with-error'
        );
        if (firstFieldError) {
          scrollTo(firstFieldError);
        }
      }
    }

    function onSubmit(data: FormState) {
      setError(null);
      setLoading(true);
      onLoading(true);
      setReceipt(false);
      api(action, 'POST', {
        [csrf.name as string]: csrf.value,
        ...data,
      })
        .then(() => {
          setLoading(false);
          onLoading(false);
          onSuccess();
          setReceipt(true);
          fromRef.current!.reset();
          csrfActions.refresh();
        })
        .catch(({ error }) => {
          setLoading(false);
          onLoading(false);
          setError(error);
          onErrorCallback(error);
          csrfActions.refresh();
        });
    }

    function renderLoader() {
      if (!loading) return null;

      return (
        <div className="loader-container">
          <Loader />
        </div>
      );
    }

    function renderError() {
      if (!error) return null;

      return <div className="global-error">{error}</div>;
    }

    function renderReceipt() {
      if (!receipt) return null;

      return (
        <div
          className="receipt"
          dangerouslySetInnerHTML={{ __html: receiptText }}
        />
      );
    }

    return (
      <div ref={containerRef} className="newsletter">
        <From
          ref={fromRef}
          {...form}
          disabled={loading || disabled}
          inactive={inactive}
          onFocus={() => {
            setReceipt(null);
          }}
          onChange={onChange}
          onSubmit={onSubmit}
          onError={onError}
        >
          {renderLoader()}
          {renderError()}
          {renderReceipt()}
        </From>
      </div>
    );
  }
);

export default Newsletter;
